import { useEffect, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import axios from "axios";
import PageHeading from "../PageHeading";
import DynamicSelect from "../DynamicSelect";
import { ToastContainer, toast } from "react-toastify";

// Reusable Image Upload Component
const ImageUpload = ({ label, file, setFile }) => {
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
  };

  const removeImage = () => {
    setFile(null);
  };

  const imageSrc =
    typeof file === "string"
      ? `https://api.muktihospital.com${file.startsWith("/") ? file : `/${file}`}`
      : file
      ? URL.createObjectURL(file)
      : null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        {imageSrc && (
            <div className="mt-4 relative inline-block">
              <img
                src={imageSrc}
                alt="Uploaded"
                className="border rounded-md w-40 h-32 object-cover"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute -top-2 -right-2  bg-red-500 text-white p-1 rounded-full"
              >
              <FaTrash size={10}/>
              </button>
            </div>
          )}
      <div className="mt-4 w-full">
        <p className="text-[10px] text-gray-600 mt-1">
          Max file size: {MAX_FILE_SIZE / 1024 / 1024}MB. Allowed types: JPEG, PNG, GIF, WEBP
        </p>
        <input
          type="file"
          accept="image/*"
          className="border p-2 rounded w-full"
          id={`upload-${label}`}
          onChange={handleFileUpload}
        />
      </div>
    </div>
  );
};

const AboutPage = () => {
  const [language, setLanguage] = useState("en");
  const [heroImage, setHeroImage] = useState(null);
  const [callbackImage, setCallbackImage] = useState(null);
  const [whoWeAre, setWhoWeAre] = useState({
    title: "",
    subtitle: "",
    tabs: [{ title: "", description: "", image: null }],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Include language as query parameter
        const res = await axios.get(`https://api.muktihospital.com/api/about?lang=${language}`);
        // Use the translation field from API response
        const data = res.data?.data?.translation;
        if (data) {
          setHeroImage(data.heroImage || null);
          setCallbackImage(data.callbackImage || null);
          setWhoWeAre({
            title: data.whoWeAre?.title || "",
            subtitle: data.whoWeAre?.subtitle || "",
            tabs: data.whoWeAre?.tabs || [{ title: "", description: "", image: null }],
          });
        }
      } catch (error) {
        console.error("Error loading data", error);
      }
    };
    fetchData();
  }, [language]);

  const handleWhoWeAreChange = (e) => {
    setWhoWeAre({ ...whoWeAre, [e.target.name]: e.target.value });
  };

  const handleTabChange = (index, field, value) => {
    const updatedTabs = [...whoWeAre.tabs];
    updatedTabs[index][field] = value;
    setWhoWeAre({ ...whoWeAre, tabs: updatedTabs });
  };

  const handleTabImageUpload = (index, file) => {
    const updatedTabs = [...whoWeAre.tabs];
    updatedTabs[index].image = file;
    setWhoWeAre({ ...whoWeAre, tabs: updatedTabs });
  };

  const removeTab = (index) => {
    const updatedTabs = whoWeAre.tabs.filter((_, i) => i !== index);
    setWhoWeAre({ ...whoWeAre, tabs: updatedTabs });
  };

  const addTab = () => {
    setWhoWeAre({
      ...whoWeAre,
      tabs: [...whoWeAre.tabs, { title: "", description: "", image: null }],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("language", language);
    formData.append("title", whoWeAre.title);
    formData.append("subtitle", whoWeAre.subtitle);
    if (heroImage instanceof File) formData.append("heroImage", heroImage);
    if (callbackImage instanceof File) formData.append("callbackImage", callbackImage);

    const tabData = whoWeAre.tabs.map((tab) => ({
      title: tab.title,
      description: tab.description,
      image: null,
    }));
    formData.append("tabs", JSON.stringify(tabData));

    whoWeAre.tabs.forEach((tab) => {
      if (tab.image instanceof File) {
        formData.append("tabs", tab.image);
      }
    });

    try {
      await axios.post("https://api.muktihospital.com/api/about", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("About Page saved successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong while saving");
    } finally {
      setLoading(false);
    }
  };

       // ----------------------------------------------------------------
  //  B) Language Switch (English <-> Bangla)
  // ----------------------------------------------------------------
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'bn', label: 'Bangla' }
  ];

  const handleLanguageChange = (selectedOption) => {
    setLanguage(selectedOption.value);
  };


    // ─────────────────────────────────────────────────────────────────────────────
  // Render form
  // ─────────────────────────────────────────────────────────────────────────────
  const breadcrumbs = [
    { label: 'Dashboard', url: '/' },
    { label: 'Manage Sections'},
    { label: 'About Us' }
  ];

  return (
    <div className=" bg-gray-50">
       <ToastContainer position="top-right" autoClose={3000} />
      <PageHeading title="Manage About Page" breadcrumbs={breadcrumbs} />

      {/* Language Selection */}
    <div className="mb-6 flex">
      <DynamicSelect
          options={languageOptions}  // Pass options for languages
          value={languageOptions.find(option => option.value === language)}
          onChange={handleLanguageChange}
          isSearchable={false}
          placeholder="Select a language"
        />
    </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white shadow-sm rounded-lg mt-5">
          <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Hero Section
            </h2>
          </div>
          <div className="p-5">
            <ImageUpload label="Upload Background Image" file={heroImage} setFile={setHeroImage} />
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg mt-5">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Request a Callback Section
            </h2>
          </div>
          <div className="p-5">
            <ImageUpload label="Upload Callback Image" file={callbackImage} setFile={setCallbackImage} />
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg mt-5">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Who We Are
            </h2>
          </div>
          <div className="p-5">
          <input
            type="text"
            name="title"
            className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            value={whoWeAre.title}
            onChange={handleWhoWeAreChange}
            placeholder="Enter section title"
          />
          <input
            type="text"
            name="subtitle"
            className="w-full px-4 py-2 border rounded-md mt-2 focus:ring-2 focus:ring-blue-500"
            value={whoWeAre.subtitle}
            onChange={handleWhoWeAreChange}
            placeholder="Enter section subtitle"
          />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 mt-5 gap-5">
        {whoWeAre.tabs.map((tab, index) => (
            <div key={index} className="mb-4 border p-4 rounded-lg bg-white relative">
              <div className="flex justify-between items-center">
                <h4 className="text-md font-medium">Tab {index + 1}</h4>
                {whoWeAre.tabs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTab(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FaTrash size={12}/>
                  </button>
                )}
              </div>

              <input
                type="text"
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 mt-2"
                value={tab.title}
                onChange={(e) => handleTabChange(index, "title", e.target.value)}
                placeholder="Enter tab title"
              />
              <textarea
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 mt-2"
                value={tab.description}
                onChange={(e) => handleTabChange(index, "description", e.target.value)}
                rows={6}
                placeholder="Enter tab description"
              />
              <ImageUpload
                label={`Upload Tab ${index + 1} Image`}
                file={tab.image}
                setFile={(file) => handleTabImageUpload(index, file)}
              />
            </div>
          ))}
        </div>
        <button
            type="button"
            onClick={addTab}
            className="mt-3 text-blue-600 text-sm hover:text-blue-700 flex items-center gap-1"
          >
            <FaPlus size={10}/> Add More Tabs
          </button>
          </div>
        </div>
        {/* SEO Configuration */}
        {/* <div className="bg-white shadow-sm rounded-lg mt-5">
             <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  SEO Configuration
                </h2>
              </div>
            <div className="p-5 grid grid-cols-2 gap-6">
             
                <div>
                  <label className="label">Meta Title</label>
                  <input
                    type="text"
                    name="metaTitle"
                    className="input-field"
                    placeholder="Enter Meta Title"
                    
                  />
                </div>

                <div>
                  <label className="label">Meta Description</label>
                  <input
                    type="text"
                    name="metaDescription"
                    className="input-field"
                    placeholder="Enter Meta Description"
                   
                  />
                </div>
            </div>
         </div> */}

        <button
          type="submit"
          disabled={loading}
          className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </form>
    </div>
  );
};

export default AboutPage;
